/**
 * Trust evidence hook (v1.1-A2 / Phase B1) — fetches real
 * evidence_artifacts rows and adapts them into the mock UI's Evidence
 * VM shape so the existing pixel-perfect /trust/evidence page can
 * render them without any JSX changes.
 *
 * Fields not covered by the A2 schema (e.g. controlMappings,
 * chainOfCustody, ownerName) are filled with sensible empty defaults
 * so the UI degrades gracefully rather than displaying placeholders.
 */

import { useQuery } from '@tanstack/react-query';
import { apiClient } from './api-client';
import type {
  Evidence,
  EvidenceType,
  EvidenceSource,
  FrameworkId,
} from './mock-trust-data';

// A3 uses hyphenated framework ids (cmmc-l2, eu-ai-act, nist-ai-rmf);
// the mock UI uses underscored ones. Map server → UI.
const FRAMEWORK_ID_SERVER_TO_UI: Record<string, FrameworkId> = {
  itar: 'itar',
  'cmmc-l2': 'cmmc_l2',
  hipaa: 'hipaa',
  gdpr: 'gdpr',
  'eu-ai-act': 'iso42001',
  'nist-ai-rmf': 'nist_ai_rmf',
  osha: 'iso27001',
  sox: 'soc2',
};

// ─── Server VM (mirror of governance-evidence.ts ArtifactSchema) ──

export interface ServerEvidenceArtifact {
  id: string;
  artifactType: string;
  frameworkId: string | null;
  controlId: string | null;
  title: string;
  description: string | null;
  sourceKind: string;
  storageUri: string | null;
  storageSizeBytes: number | null;
  hashChainAnchor: string | null;
  collectedBy: string;
  collectedAt: string;
  classification: string | null;
  retentionUntil: string | null;
  signoffRequired: boolean;
  signoffBy: string | null;
  signoffAt: string | null;
  signoffNotes: string | null;
  metadata: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
}

interface ListResponse {
  // governance-evidence.ts wraps rows in `data`; keep `artifacts` as a
  // tolerated legacy alias if older callers still use it.
  data?: ServerEvidenceArtifact[];
  artifacts?: ServerEvidenceArtifact[];
}

// ─── Adapter: server row → mock Evidence VM ───────────────────────

const ARTIFACT_TYPE_TO_VM: Record<string, EvidenceType> = {
  audit_log: 'log',
  configuration_snapshot: 'configuration',
  attestation_signoff: 'approval',
  screenshot: 'configuration',
  document_upload: 'policy',
  compliance_report: 'risk',
  incident_response: 'incident',
  access_review: 'log',
  training_record: 'training',
  policy_acknowledgment: 'policy',
  penetration_test: 'test',
  risk_assessment: 'risk',
};

const SOURCE_KIND_TO_VM: Record<string, EvidenceSource> = {
  audit_chain_export: 'argus_primitive',
  manual_upload: 'manual_upload',
  auto_generated: 'auto_collected',
  system_snapshot: 'auto_collected',
  sso_audit: 'api_pull',
  config_signoff: 'manual_upload',
  webhook_capture: 'api_pull',
};

function vmStatus(retentionUntil: string | null): Evidence['status'] {
  if (!retentionUntil) return 'current';
  const due = new Date(retentionUntil).getTime();
  const now = Date.now();
  if (Number.isNaN(due)) return 'current';
  if (due < now) return 'expired';
  if (due - now < 30 * 24 * 60 * 60 * 1000) return 'expiring_soon';
  return 'current';
}

export function adaptArtifactToEvidence(a: ServerEvidenceArtifact): Evidence {
  return {
    id: a.id,
    title: a.title,
    description: a.description ?? '',
    type: ARTIFACT_TYPE_TO_VM[a.artifactType] ?? 'policy',
    source: SOURCE_KIND_TO_VM[a.sourceKind] ?? 'manual_upload',
    fileName: a.storageUri ? a.storageUri.split('/').pop() ?? null : null,
    fileSize: a.storageSizeBytes,
    mimeType: null,
    uploadedByUserId: a.collectedBy,
    uploadedByName: '—',
    uploadedAt: a.collectedAt,
    effectiveDate: a.collectedAt,
    expirationDate: a.retentionUntil,
    reviewCycle: 'never',
    nextReviewAt: null,
    tags: [],
    controlMappings:
      a.frameworkId && a.controlId && FRAMEWORK_ID_SERVER_TO_UI[a.frameworkId]
        ? [
            {
              framework: FRAMEWORK_ID_SERVER_TO_UI[a.frameworkId]!,
              controlId: a.controlId,
              controlName: a.controlId,
              relevance: 'primary',
            },
          ]
        : [],
    ownerUserId: a.collectedBy,
    ownerName: '—',
    auditorVisible: true,
    redactedVersion: false,
    versionNumber: 1,
    previousVersionId: null,
    chainOfCustody: a.signoffAt && a.signoffBy
      ? [
          {
            userId: a.signoffBy,
            userName: '—',
            action: 'signed_off',
            timestamp: a.signoffAt,
          },
        ]
      : [],
    argusAiPrimitive: null,
    status: vmStatus(a.retentionUntil),
  };
}

// ─── Hook ─────────────────────────────────────────────────────────

export function useTrustEvidence() {
  return useQuery<ListResponse, Error, Evidence[]>({
    queryKey: ['trust', 'evidence'],
    queryFn: () => apiClient.get<ListResponse>('/governance/evidence'),
    select: (res) => (res.data ?? res.artifacts ?? []).map(adaptArtifactToEvidence),
  });
}

// useAutoCollectEvidence(): POST /governance/evidence/auto-collect + invalidate ['trust','evidence']. See file.
